import React from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import { ring } from '../../utils/format';
import { metaColor } from '../../utils/meta';
import type { MetaFila } from '../../types/indicadores.types';

export interface MedidoresMetaProps {
  cumplimientoMonto: number | null;
  cumplimientoPares: number | null;
  filas: MetaFila[];
}

const RING_CIRC = 2 * Math.PI * 34;

interface AnilloProps {
  etiqueta: string;
  glosa: string;
  /** Fracción 0..1, o `null` si no hay denominador. */
  valor: number | null;
  color: string;
  texto: string;
  /** Disposición de teléfono: el anillo ocupa una fila entera. */
  esMovil: boolean;
}

const Anillo: React.FC<AnilloProps> = ({
  etiqueta,
  glosa,
  valor,
  color,
  texto,
  esMovil,
}) => (
  <div
    style={{
      background: colors.card,
      border: `1px solid ${colors.border}`,
      borderRadius: 16,
      // Menos acolchado lateral en teléfono (14 en vez de 20): son 12 px más
      // para el texto de la derecha, que es el que se queda sin sitio.
      padding: esMovil ? '14px' : '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: esMovil ? 14 : 18,
      boxShadow: '0 1px 2px rgba(20,28,54,.05)',
      flex: 1,
      // `minWidth: 240` es lo que en escritorio obliga a los tres anillos a
      // envolverse antes de estrujarse. En teléfono ya van uno por fila, y ese
      // mínimo impediría que la tarjeta se encogiera por debajo de 240 px:
      // sumado al acolchado, desbordaría los 390 px de pantalla.
      minWidth: esMovil ? 0 : 240,
      width: esMovil ? '100%' : undefined,
    }}
  >
    <div style={{ position: 'relative', width: 82, height: 82, flex: 'none' }}>
      <svg width="82" height="82" viewBox="0 0 84 84">
        <circle cx="42" cy="42" r="34" fill="none" stroke={colors.ringTrack} strokeWidth={8} />
        <circle
          cx="42"
          cy="42"
          r="34"
          fill="none"
          stroke={color}
          strokeWidth={8}
          strokeLinecap="round"
          // El anillo se llena hasta el 100 % aunque se supere la meta: un aro
          // que da más de una vuelta no se puede leer. El exceso lo dice el
          // número del centro.
          strokeDasharray={ring(valor === null ? null : valor * 100, RING_CIRC)}
          transform="rotate(-90 42 42)"
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // El anillo mide 82 px en las dos disposiciones, así que la cifra del
          // centro no necesita crecer; sólo se le sube un punto en teléfono,
          // que es donde se mira de lejos y sin poder acercar la vista.
          fontSize: esMovil ? 16 : 15,
          fontWeight: 800,
          color: colors.texto.primary,
        }}
      >
        {texto}
      </div>
    </div>
    {/* `minWidth: 0` en el hijo que lleva el texto: sin él, el flex se niega a
        encogerlo por debajo del ancho de su palabra más larga y la tarjeta
        entera se sale de la pantalla. */}
    <div style={{ minWidth: esMovil ? 0 : undefined }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: colors.texto.strong }}>{etiqueta}</div>
      <div style={{ fontSize: 12, color: colors.texto.tertiary, marginTop: 3, lineHeight: 1.35 }}>
        {glosa}
      </div>
    </div>
  </div>
);

/**
 * Los tres medidores del tablero de Presupuesto.
 *
 * ⚠️ NO reutilizan `tasaColor`: ese colorea ALTO = MALO a propósito, porque mide
 * venta que se fue por fuera del sistema. Aquí alto es bueno, así que va
 * `metaColor`. Reutilizarlo habría pintado de rojo a la tienda que superó su
 * meta, que es exactamente al revés.
 *
 * Cada glosa dice QUÉ dividió: un porcentaje sin denominador es una opinión.
 */
const MedidoresMeta: React.FC<MedidoresMetaProps> = ({
  cumplimientoMonto,
  cumplimientoPares,
  filas,
}) => {
  const esMovil = useEsMovil();
  const conMeta = filas.filter((f) => f.conMeta && f.cumplimientoMonto !== null);
  const enMeta = conMeta.filter((f) => (f.cumplimientoMonto ?? 0) >= 1).length;
  const fraccionTiendas = conMeta.length > 0 ? enMeta / conMeta.length : null;

  const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(0)}%`);

  return (
    <div
      style={{
        display: 'flex',
        // Un anillo por fila en teléfono. Con `flexWrap` solo, el primero se
        // quedaría en sus 240 px de mínimo y los tres acabarían desalineados.
        flexDirection: esMovil ? 'column' : 'row',
        gap: esMovil ? 12 : 14,
        flexWrap: 'wrap',
      }}
    >
      <Anillo
        etiqueta="Cumplimiento en pesos"
        glosa="Venta neta ÷ meta del periodo"
        valor={cumplimientoMonto}
        color={metaColor(cumplimientoMonto)}
        texto={pct(cumplimientoMonto)}
        esMovil={esMovil}
      />
      <Anillo
        etiqueta="Cumplimiento en pares"
        glosa="Pares vendidos ÷ pares de la meta"
        valor={cumplimientoPares}
        color={metaColor(cumplimientoPares)}
        texto={pct(cumplimientoPares)}
        esMovil={esMovil}
      />
      <Anillo
        etiqueta="Tiendas en meta o arriba"
        glosa={`${enMeta} de ${conMeta.length} tiendas con meta cargada`}
        valor={fraccionTiendas}
        color={metaColor(fraccionTiendas)}
        texto={conMeta.length === 0 ? '—' : String(enMeta)}
        esMovil={esMovil}
      />
    </div>
  );
};

export default MedidoresMeta;
